import { useEffect, useState } from "react";
import api from "../api/axios";
import {
  X,
  RotateCcw,
  ChevronRight,
  Minimize2,
  Move,
  Info,
} from "lucide-react";
import MapboxPlano from "../components/MapBoxPlano";
import SidebarPropiedad from "../components/SidebarPropiedad";
import ModalPropiedad from "../components/ModalPropiedad";
import ModalFiltros from "../components/ModalFiltros";

export default function VerPlano() {
  const [propiedades, setPropiedades] = useState([]);
  const [propiedadSeleccionada, setPropiedadSeleccionada] = useState(null);
  const [propiedadEnfocadaMapa, setPropiedadEnfocadaMapa] = useState(null);
  const [solicitudCentradoMapa, setSolicitudCentradoMapa] = useState(0);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [modalPropiedadMovilAbierto, setModalPropiedadMovilAbierto] =
    useState(false);
  const [modalFiltrosAbierto, setModalFiltrosAbierto] = useState(false);
  const [filtros, setFiltros] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);

  useEffect(() => {
    const cargarPropiedades = async () => {
      try {
        setCargando(true);
        setError(null);

        const res = await api.get("/properties");

        // Transformar propiedades para Mapbox
        const propiedadesTransformadas = res.data.map((prop, index) => {
          // Si no tiene coordenadas, generar unas alrededor de Mérida
          let lat, lng;

          if (prop.pos_x && prop.pos_y) {
            // Convertir pos_x/pos_y (porcentajes) a coordenadas aproximadas
            // Mérida aprox: 20.5° a 21.4° N, -89.8° a -89.4° W
            lat = 20.5 + (prop.pos_y / 100) * 0.9; // Convertir % a latitud
            lng = -89.8 + (prop.pos_x / 100) * 0.4; // Convertir % a longitud
          } else {
            // Generar coordenadas aleatorias en Mérida
            const zonas = {
              norte: { lat: 21.05, lng: -89.62 },
              centro: { lat: 20.967, lng: -89.592 },
              sur: { lat: 20.88, lng: -89.62 },
            };
            const zonasArray = Object.values(zonas);
            const zona = zonasArray[index % zonasArray.length];
            lat = zona.lat + (Math.random() - 0.5) * 0.02;
            lng = zona.lng + (Math.random() - 0.5) * 0.02;
          }

          return {
            ...prop,
            id: prop.id || `prop-${index}`,
            coordinates: [lng, lat], // [longitud, latitud] para Mapbox
            lat,
            lng,
            precio: prop.precio || 0,
            tipo_operacion: prop.caracteristicas.tipo_operacion,
            zona: prop.zona || determinarZona(lat),
            caracteristicas: prop.caracteristicas || {},
          };
        });

        setPropiedades(propiedadesTransformadas);
      } catch (err) {
        console.error("Error cargando propiedades:", err);
        setError("No se pudieron cargar las propiedades");

        // Datos de ejemplo para desarrollo
        const propiedadesDemo = generarPropiedadesDemo();
        setPropiedades(propiedadesDemo);
      } finally {
        setCargando(false);
      }
    };

    cargarPropiedades();

    // Ocultar instrucciones después de 8 segundos
    const timer = setTimeout(() => {
      setShowInstructions(false);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  const determinarZona = (lat) => {
    if (lat > 21.0) return "Norte";
    if (lat > 20.9) return "Centro";
    return "Sur";
  };

  const generarPropiedadesDemo = () => {
    const demos = [];
    const operaciones = ["venta", "renta"];

    for (let i = 0; i < 25; i++) {
      const zonaIndex = i % 3;
      const zonas = [
        { name: "Norte", lat: 21.05, lng: -89.62 },
        { name: "Centro", lat: 20.967, lng: -89.592 },
        { name: "Sur", lat: 20.88, lng: -89.62 },
      ];
      const zona = zonas[zonaIndex];

      demos.push({
        id: `demo-${i}`,
        titulo: `Propiedad ${i + 1} en ${zona.name}`,
        ubicacion: `Calle ${i + 1}, ${zona.name}, Mérida`,
        precio: Math.floor(Math.random() * 5000000) + 1000000,
        tipo_operacion: operaciones[Math.floor(Math.random() * 2)],
        lat: zona.lat + (Math.random() - 0.5) * 0.02,
        lng: zona.lng + (Math.random() - 0.5) * 0.02,
        coordinates: [
          zona.lng + (Math.random() - 0.5) * 0.02,
          zona.lat + (Math.random() - 0.5) * 0.02,
        ],
        zona: zona.name,
        caracteristicas: {
          recamaras: Math.floor(Math.random() * 5) + 1,
          banos: Math.floor(Math.random() * 4) + 1,
          area: Math.floor(Math.random() * 300) + 50,
        },
        foto_principal: "/uploads/default.jpg",
      });
    }

    return demos;
  };

  const aplicarFiltros = (nuevoFiltro) => {
    setFiltros(nuevoFiltro);
    setModalFiltrosAbierto(false);
  };

  // Filtrado optimizado para coordenadas reales
  const propiedadesFiltradas = (() => {
    let resultado = [...propiedades];

    if (filtros) {
      resultado = resultado.filter((p) => {
        const c = p.caracteristicas || {};

        // Búsqueda por texto
        if (
          filtros.busqueda &&
          !p.ubicacion.toLowerCase().includes(filtros.busqueda.toLowerCase())
        ) {
          return false;
        }

        // Operación
        if (
          filtros.tipo_operacion &&
          p.tipo_operacion !== filtros.tipo_operacion
        ) {
          return false;
        }

        // Precio
        if (filtros.precioMin && p.precio < filtros.precioMin) {
          return false;
        }
        if (filtros.precioMax && p.precio > filtros.precioMax) {
          return false;
        }

        // Zona
        if (filtros.zona && p.zona !== filtros.zona) {
          return false;
        }

        // Características
        if (
          filtros.recamaras &&
          Number(c.recamaras) < Number(filtros.recamaras)
        ) {
          return false;
        }
        if (filtros.banos && Number(c.banos) < Number(filtros.banos)) {
          return false;
        }

        // Características booleanas
        const cfil = filtros.caracteristicas || {};
        for (const key in cfil) {
          if (cfil[key] && !c[key]) {
            return false;
          }
        }

        return true;
      });

      // Ordenamiento
      if (filtros.ordenarPor) {
        resultado.sort((a, b) => {
          switch (filtros.ordenarPor) {
            case "precio_desc":
              return b.precio - a.precio;
            case "precio_asc":
              return a.precio - b.precio;
            case "recamaras":
              return (
                (b.caracteristicas?.recamaras || 0) -
                (a.caracteristicas?.recamaras || 0)
              );
            case "area":
              return (
                (b.caracteristicas?.area || 0) - (a.caracteristicas?.area || 0)
              );
            default:
              return 0;
          }
        });
      }
    }

    return resultado;
  })();

  const handleSeleccionarPropiedad = (prop) => {
    setPropiedadSeleccionada(prop);
    setMostrarModal(true);
  };

  const handleCentrarPropiedadEnMapa = (prop) => {
    setPropiedadEnfocadaMapa(prop);
    setSolicitudCentradoMapa((valor) => valor + 1);
  };

  const handleDeseleccionarPropiedad = () => {
    setMostrarModal(false);
    setModalPropiedadMovilAbierto(false);
    setPropiedadSeleccionada(null);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      if (propiedadSeleccionada) return;

      if (!sidebarMinimized) {
        event.preventDefault();
        setSidebarMinimized(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [propiedadSeleccionada, sidebarMinimized]);

  const resetSidebar = () => {
    localStorage.removeItem("sidebar-width");
    localStorage.removeItem("sidebar-position");
    localStorage.removeItem("sidebar-minimized");
    setSidebarMinimized(false);
    // Recargar la página para aplicar cambios
    setTimeout(() => window.location.reload(), 100);
  };

  if (cargando) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium text-slate-700">
            Cargando plano interactivo
          </p>
          <p className="text-sm text-slate-500 mt-2">Inicializando Mapbox...</p>
        </div>
      </div>
    );
  }

  if (error && propiedades.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl text-red-500">!</span>
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            Error al cargar
          </h3>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-screen h-screen overflow-hidden bg-slate-50">
      {/* Mapa Mapbox - Fondo */}
      <div className="absolute inset-0 z-0">
        <MapboxPlano
          propiedades={propiedadesFiltradas}
          onSeleccionarPropiedad={handleSeleccionarPropiedad}
          propiedadSeleccionada={propiedadSeleccionada}
          propiedadEnfocada={propiedadEnfocadaMapa}
          solicitudCentradoMapa={solicitudCentradoMapa}
        />
      </div>

      {/* Sidebar */}
      <SidebarPropiedad
        propiedadesFiltradas={propiedadesFiltradas}
        onOpenFiltros={() => setModalFiltrosAbierto(true)}
        filtrosAplicados={!!filtros}
        ordenActual={filtros?.ordenarPor || ""}
        onSeleccionar={handleSeleccionarPropiedad}
        onBuscar={(texto) =>
          setFiltros((prev) => ({ ...prev, busqueda: texto }))
        }
        busqueda={filtros?.busqueda || ""}
        operacionSeleccionada={filtros?.operacion || ""}
        onCambiarOperacion={(op) =>
          setFiltros((prev) => ({ ...prev, operacion: op }))
        }
        onCambiarOrden={(ord) =>
          setFiltros((prev) => ({ ...prev, ordenarPor: ord }))
        }
        isMinimized={sidebarMinimized}
        onToggleMinimize={() => setSidebarMinimized((prev) => !prev)}
        onDeseleccionarPropiedad={handleDeseleccionarPropiedad}
        onCentrarPropiedad={handleCentrarPropiedadEnMapa}
        onVerPropiedad={() => setModalPropiedadMovilAbierto(true)}
        propiedadSeleccionada={propiedadSeleccionada}
        mostrarModal={mostrarModal}
      />

      {/* Botón para mostrar sidebar si está minimizado */}
      {sidebarMinimized && (
        <button
          onClick={() => setSidebarMinimized(false)}
          className="fixed top-6 left-6 z-30 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-blue-700 transition-colors animate-in fade-in duration-300 hover:scale-105"
          title="Mostrar panel de propiedades"
        >
          <ChevronRight size={20} />
        </button>
      )}

      {/* Instrucciones de uso flotantes */}
      {showInstructions && !sidebarMinimized && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 bg-black/80 text-white text-xs px-6 py-3 rounded-2xl backdrop-blur-sm animate-in fade-in duration-500 shadow-xl border border-white/10">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Move size={12} className="text-blue-300" />
              <span>Arrastra desde la barra superior</span>
            </div>
            <span className="text-slate-400">•</span>
            <div className="flex items-center gap-2">
              <div className="w-2 h-4 bg-gradient-to-r from-blue-400 to-blue-300 rounded-sm"></div>
              <span>Borde derecho para redimensionar</span>
            </div>
            <span className="text-slate-400">•</span>
            <button
              onClick={() => setSidebarMinimized(true)}
              className="flex items-center gap-1 hover:text-blue-300 transition-colors"
            >
              <Minimize2 size={12} /> Minimizar
            </button>
          </div>
        </div>
      )}

      {/* Botón flotante para resetear sidebar */}
      <button
        onClick={resetSidebar}
        className="fixed bottom-6 right-6 z-30 w-10 h-10 bg-white/80 backdrop-blur-sm border border-slate-200/60 text-slate-600 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-300 hover:shadow-xl hover:scale-110"
        title="Restablecer panel"
      >
        <RotateCcw size={16} />
      </button>

      {/* Botón flotante para mostrar instrucciones */}
      <button
        onClick={() => setShowInstructions(!showInstructions)}
        className="fixed bottom-6 left-6 z-30 w-10 h-10 bg-white/80 backdrop-blur-sm border border-slate-200/60 text-slate-600 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-300 hover:shadow-xl hover:scale-110"
        title="Mostrar instrucciones"
      >
        <Info size={16} />
      </button>

      {/* Modal de Propiedad */}
      {/*mostrarModal && propiedadSeleccionada && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMostrarModal(false)}
          />
          <div className="relative z-50 w-full max-w-4xl">
            <ModalPropiedad
              propiedad={propiedadSeleccionada}
              onClose={() => setMostrarModal(false)}
            />
          </div>
        </div>
      )*/}

      {/* Modal de Filtros */}
      {modalFiltrosAbierto && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
            onClick={() => setModalFiltrosAbierto(false)}
          />

          <div className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div>
                <h3 className="text-xl font-serif text-slate-900 italic">
                  Refinar Búsqueda
                </h3>
                <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1">
                  Parámetros de Selección
                </p>
              </div>
              <button
                onClick={() => setModalFiltrosAbierto(false)}
                className="p-2 hover:bg-white rounded-full transition-shadow shadow-sm border border-slate-100"
              >
                <X size={18} className="text-slate-400" />
              </button>
            </div>

            <div className="p-8 max-h-[60vh] overflow-y-auto">
              <ModalFiltros
                filtrosIniciales={filtros}
                onAplicarFiltros={aplicarFiltros}
              />
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-4">
              <button
                onClick={() => {
                  setFiltros(null);
                  setModalFiltrosAbierto(false);
                }}
                className="flex-1 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors"
              >
                <RotateCcw size={14} /> Limpiar Todo
              </button>
            </div>
          </div>
        </div>
      )}

      {modalPropiedadMovilAbierto && propiedadSeleccionada && (
        <ModalPropiedad
          propiedad={propiedadSeleccionada}
          onClose={() => setModalPropiedadMovilAbierto(false)}
          isModal={true}
          showCloseButton={true}
          fullScreen
        />
      )}
    </div>
  );
}
